import {
  COMPONENT_TYPES,
  CountryIso3166,
  DisplaySettings,
  LangageIso6391,
  MaterialType,
  Prisma,
} from "@tfg/db"
import { z } from "zod"

import { LanguageIso6391Schema } from "@tfg/types/schemas/enum"

export const NOT_ENTERED_YET = "not-entered-yet"
export const NOT_VALIDATED_YET = "not-validated-yet"

export type NotEnteredYet = typeof NOT_ENTERED_YET
export type NotValidatedYet = typeof NOT_VALIDATED_YET
export type MissingInfoCause = NotEnteredYet | NotValidatedYet

type NonApplicableCause =
  | "irrelevant-with-leather"
  | "irrelevant-with-duvet"
  | "missing-main-component"
  | "maincomponent-cant-be-accessory"
  | "missing-main-component"
  | "maincomponent-cant-be-wood"
  | "not-applicable"
export type MissingCountryCause = MissingInfoCause | NonApplicableCause

export type CountryInfo<C extends MissingCountryCause> =
  | {
      country: CountryIso3166
      cause?: never
    }
  | {
      country?: never
      cause: C
    }

export interface AgecMaterialDetails {
  type: MaterialType
  proportion: number
  itemId: number
  translations: ProductSheetTranslations
}

export interface AgecOperationDetails {
  id: string
  type: "product" | "component" | "material"
  country: CountryInfo<MissingInfoCause>
  activityName: string
  itemId: number
  childrenRef: string[]
  certificates: AgecCertificateDetails[]
}

export interface AgecCertificateDetails {
  name: string
  displayName: string | null
}

export interface AgecComponentDetails {
  type: string
  itemId: number
  materials: AgecMaterialDetails[]
  certificates: unknown[]
  translations: ProductSheetTranslations
}

export const ComponentDetailsSchema = z.object({
  componentType: z.nativeEnum(COMPONENT_TYPES),
  content: z.record(LanguageIso6391Schema, z.any()),
  order: z.number(),
  settings: z.record(z.string(), z.string()).optional(),
})
export type ComponentDetails = z.infer<typeof ComponentDetailsSchema>

export interface OrganizationPublicConfig {
  name: string
  id: string
  config: {
    displayCustomAgecPage?: boolean | undefined
    displaySetting?:
      | (DisplaySettings & {
          components: ComponentDetails[]
          languages: LangageIso6391[]
        })
      | undefined
  }
}

export interface PublicDisplaySettings {
  slug: string
  outputLogoUrl?: string | undefined
  outputBackgroundColor?: string | undefined
  outputHeaderBackgroundColor?: string | undefined
  outputTextBodyColor?: string | undefined
  outputTitlesColor?: string | undefined
  outputFontFamily?: string | undefined
  filters: Prisma.JsonValue
  companySlug: string
  languages: LangageIso6391[]
}

export enum ProductSheetLanguages {
  EN = "EN",
  FR = "FR",
  DE = "DE",
  IT = "IT",
  ES = "ES",
  NL = "NL",
}

export type ProductSheetTranslations = {
  id: number
  originalText: string
  FR: string
  EN: string
  DE: string
  IT: string
  ES: string
  NL: string
}

export type AgecActivityCountries = {
  activityName: string | "error"
  country: CountryInfo<MissingCountryCause>
}[]

export enum RecycledMaterialPercentageCause {
  BY_INGESTED_PRODUCT_RECYCLED_PERCENTAGE = "by-ingested-product-recycled-percentage",
  BY_INGESTED_MATERIAL_COMPOSITION_RECYCLED_PERCENTAGE = "by-ingested-material-composition-recycled-percentage",
  BY_ENTERED_COMPONENTS_RECYCLED_PERCENTAGE = "by-entered-components-recycled-percentage",
  BY_MIXED_COMPONENTS_RECYCLED_PERCENTAGE = "by-mixed-components-recycled-percentage",
  NOT_CALCULATED = "not-calculated",
}

export type RecycledFibers = {
  percentage: number
  cause: RecycledMaterialPercentageCause
}

export namespace AGECResponses {
  export type GenericAgecData = {
    productName: string
    type: string
    brandReference: string
    color: string | null
    brand: string | null
    withPrimaryPackaging: boolean
    primaryPackagingRecyclable: boolean
    primaryPackagingRecycledPercentage: number | null
    primaryPackagingHazardousSubstances: boolean
    operationChainName: string
    company: OrganizationPublicConfig
    operations: AgecOperationDetails[]
    recycledFibers: RecycledFibers
    picture: string | null
    releasesMicroPlastic: boolean | "missing-main-component"
    agecActivityCountries: AgecActivityCountries
    components: AgecComponentDetails[]
    lastUpdate: string
  }
}

export interface PackagingInfo {
  primaryPackagingRecycledPercentage: number | null
  withPrimaryPackaging: boolean
  primaryPackagingRecyclable: boolean
}
